"use client";

import { Button } from "@/components/ui/button";
import {
  Activity,
  ArrowRight,
  Brain,
  Clapperboard,
  Download,
  FileText,
  FolderOpen,
  Image,
  Languages,
  LayoutDashboard,
  Lightbulb,
  Mic,
  MonitorPlay,
  Pencil,
  Plug,
  Scissors,
  SearchCheck,
  Sparkles,
  Upload,
  Volume2,
  Zap,
} from "lucide-react";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { PublicLink as Link } from "@/components/public/PublicLink";
import { rewriteMarketingHref } from "@/lib/seo/public-seo";

interface FeatureStep {
  titleKey: string;
  detailKey: string;
}

interface FeatureCardData {
  key: string;
  icon: React.ElementType;
  route: string;
  colorScheme: "orange" | "green";
  steps: FeatureStep[];
}

interface FeatureGroup {
  id: string;
  groupKey: string;
  features: FeatureCardData[];
}

const FEATURE_GROUPS: FeatureGroup[] = [
  {
    id: "create-optimize",
    groupKey: "createOptimize",
    features: [
      {
        key: "projectWorkflow",
        icon: Upload,
        route: "/projects/new",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "reoptimizeWorkflow",
        icon: Sparkles,
        route: "/reoptimize",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "diagnoseWorkflow",
        icon: Activity,
        route: "/diagnose",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "precisionTrimWorkflow",
        icon: Clapperboard,
        route: "/precision-trim",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "thumbnailStudioWorkflow",
        icon: Image,
        route: "/thumbnails",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "thumbnailEditorWorkflow",
        icon: Pencil,
        route: "/thumbnails",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
    ],
  },
  {
    id: "repurpose-localize",
    groupKey: "repurposeLocalize",
    features: [
      {
        key: "translationWorkflow",
        icon: Languages,
        route: "/translate",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "dubbingWorkflow",
        icon: Volume2,
        route: "/dubbing",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "autoclipsWorkflow",
        icon: Scissors,
        route: "/auto-clips",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "shortsDeliveryWorkflow",
        icon: Download,
        route: "/auto-clips",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
    ],
  },
  {
    id: "analyze-strategize",
    groupKey: "analyzeStrategizeVerified",
    features: [
      {
        key: "intelligenceWorkflow",
        icon: Brain,
        route: "/intelligence",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "videoIdeasWorkflow",
        icon: Lightbulb,
        route: "/video-ideas",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "channelDescriptionWorkflow",
        icon: FileText,
        route: "/channel-description-generator",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "midrollWorkflow",
        icon: MonitorPlay,
        route: "/midroll-ads",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "whyWorkflow",
        icon: SearchCheck,
        route: "/why",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
    ],
  },
  {
    id: "manage-operate",
    groupKey: "manageOperate",
    features: [
      {
        key: "dashboardWorkflow",
        icon: LayoutDashboard,
        route: "/dashboard",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "projectsLibraryWorkflow",
        icon: FolderOpen,
        route: "/projects",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "accountsWorkflow",
        icon: Plug,
        route: "/settings/accounts",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "voiceWorkflow",
        icon: Mic,
        route: "/settings/voice",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
    ],
  },
];

const GROUP_ICONS: Record<string, React.ElementType> = {
  createOptimize: Upload,
  repurposeLocalize: Languages,
  analyzeStrategizeVerified: Brain,
  manageOperate: LayoutDashboard,
};

const FAQ_KEYS = [
  "projectRoutes",
  "youtubeRequirements",
  "translationPublishing",
  "dubbingSource",
  "exportLimits",
] as const;

function FeatureCard({
  feature,
  t,
}: {
  feature: FeatureCardData;
  t: (key: string, params?: Record<string, unknown>) => string;
}) {
  const Icon = feature.icon;
  const prefix = `features.${feature.key}`;

  return (
    <article className="border-border rounded-2xl border p-6">
      <div className="mb-5 flex items-start gap-3">
        <div className="bg-leaf-500/10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
          <Icon className="text-leaf-500 h-5 w-5" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="text-foreground mb-1 text-xl font-semibold">
            {t(`${prefix}.title`)}
          </h3>
          <p className="text-muted-foreground text-sm">
            {t(`${prefix}.subtitle`)}
          </p>
        </div>
      </div>

      <ol className="mb-5 space-y-2">
        {feature.steps.map((step, index) => (
          <li key={index} className="flex items-start gap-3">
            <span className="text-leaf-600 dark:text-leaf-400 mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center text-xs font-bold">
              {index + 1}.
            </span>
            <div>
              <span className="text-foreground text-sm font-medium">
                {t(`${prefix}.${step.titleKey}`)}
              </span>
              <span className="text-muted-foreground text-sm">
                {" — "}
                {t(`${prefix}.${step.detailKey}`)}
              </span>
            </div>
          </li>
        ))}
      </ol>

      <Link href={rewriteMarketingHref(feature.route)}>
        <Button
          variant="outline"
          size="sm"
          className="border-leaf-500/30 text-leaf-600 dark:text-leaf-400 hover:bg-leaf-500/10"
        >
          {t(`${prefix}.cta`)} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </Link>
    </article>
  );
}

export default function HowToPage() {
  const { t } = useJsonTranslation("HowTo");

  const scrollToGroup = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="flex-1 pt-24 pb-16">
      <div className="container mx-auto max-w-6xl px-4">
        {/* Hero Section */}
        <div className="mb-12 text-center">
          <p className="text-muted-foreground mb-3 text-sm tracking-widest uppercase">
            {t("hero.badge")}
          </p>
          <h1 className="text-foreground text-wrap-balance mb-4 text-4xl font-bold sm:text-5xl">
            {t("hero.title")}
          </h1>
          <p className="text-muted-foreground mx-auto max-w-3xl text-lg">
            {t("hero.subtitle")}
          </p>

          {/* Group Navigation */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {FEATURE_GROUPS.map((group) => {
              const GroupIcon = GROUP_ICONS[group.groupKey];
              return (
                <button
                  key={group.id}
                  onClick={() => scrollToGroup(group.id)}
                  className="border-border hover:bg-foreground/5 flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors"
                >
                  {GroupIcon && (
                    <GroupIcon className="h-4 w-4" aria-hidden="true" />
                  )}
                  {t(`groups.${group.groupKey}.title`)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Feature Groups */}
        {FEATURE_GROUPS.map((group) => (
          <section key={group.id} id={group.id} className="mb-16 scroll-mt-24">
            <div className="mb-8 text-center">
              <h2 className="text-foreground text-wrap-balance mb-2 text-2xl font-bold md:text-3xl">
                {t(`groups.${group.groupKey}.title`)}
              </h2>
              <p className="text-muted-foreground">
                {t(`groups.${group.groupKey}.tagline`)}
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {group.features.map((feature) => (
                <FeatureCard key={feature.key} feature={feature} t={t} />
              ))}
            </div>
          </section>
        ))}

        {/* FAQ Section */}
        <section id="faq" className="mb-16 scroll-mt-24">
          <h2 className="text-foreground text-wrap-balance mb-8 text-center text-2xl font-bold">
            {t("faq.title")}
          </h2>
          <div className="divide-border divide-y rounded-xl border">
            {FAQ_KEYS.map((faqKey) => (
              <div key={faqKey} className="p-6">
                <h3 className="text-foreground mb-2 font-semibold">
                  {t(`faq.${faqKey}.question`)}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t(`faq.${faqKey}.answer`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <div className="border-border rounded-2xl border px-8 py-12 text-center">
          <h2 className="text-foreground text-wrap-balance mb-4 text-2xl font-bold md:text-3xl">
            {t("cta.title")}
          </h2>
          <p className="text-muted-foreground mx-auto mb-8 max-w-2xl">
            {t("cta.description")}
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <Link href="/seeds">
              <Button size="lg" className="bg-leaf-500 hover:bg-leaf-600">
                {t("cta.viewPricing")}
              </Button>
            </Link>
            <Link href="/signup">
              <Button size="lg" variant="outline">
                {t("cta.getStarted")} <Zap className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
